import { useMutation } from "@tanstack/react-query";
import { Clock3, Eye, KeyRound, LockKeyhole, LogOut, MonitorCog, MonitorUp, Network, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { useAuth, useCredentials } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import { useViewSettings } from "../contexts/SettingsContext";
import { AUTO_LOGOUT_OPTIONS, normalizeAutoLogoutMinutes } from "../domain/session-timeout";
import { changePassword, errorMessage } from "../lib/api";

export function AccountPage() {
  const credentials = useCredentials();
  const { logout } = useAuth();
  const settings = useViewSettings();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const mutation = useMutation({
    mutationFn: () => changePassword(credentials, password),
    onSuccess: () => {
      notify("Heslo bylo změněno. Přihlaste se prosím znovu.", "success");
      logout();
      navigate("/auth/login");
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) return;
    mutation.mutate();
  };

  return (
    <div className="page account-page">
      <header className="page-header">
        <div>
          <span className="page-kicker">Zákaznický účet</span>
          <h1>Nastavení účtu</h1>
          <p>Zabezpečení a osobní preference administrace.</p>
        </div>
      </header>

      <div className="detail-grid">
        <section className="detail-card">
          <header className="detail-card__header">
            <div><span className="detail-card__icon"><ShieldCheck /></span><span><h2>Přihlašovací údaje</h2><p>{credentials.login}</p></span></div>
          </header>
          <form className="form-stack account-password" onSubmit={submit}>
            <label className="field">
              <span>Nové heslo</span>
              <div className="input-with-icon"><LockKeyhole /><input type="password" required minLength={8} maxLength={50} pattern="[A-Za-z0-9]+" value={password} onChange={(event) => setPassword(event.target.value)} /></div>
              <small>8–50 znaků, povolená jsou písmena a číslice.</small>
            </label>
            <label className="field">
              <span>Nové heslo znovu</span>
              <div className="input-with-icon"><KeyRound /><input type="password" required value={confirm} onChange={(event) => setConfirm(event.target.value)} /></div>
            </label>
            {password && confirm && password !== confirm && <div className="form-error">Zadaná hesla se neshodují.</div>}
            {mutation.error && <div className="form-error">{errorMessage(mutation.error)}</div>}
            <Button type="submit" variant="primary" busy={mutation.isPending} disabled={!password || password !== confirm}>Změnit heslo</Button>
          </form>
        </section>

        <section className="detail-card">
          <header className="detail-card__header">
            <div><span className="detail-card__icon"><MonitorCog /></span><span><h2>Zobrazení VPS</h2><p>Volitelné údaje v seznamu serverů</p></span></div>
          </header>
          <div className="settings-list">
            <label><span><Eye /><span><strong>Veřejná gateway IPv4</strong><small>Zobrazit IPv4 adresu gateway položky veřejného rozhraní.</small></span></span><input type="checkbox" checked={settings.showPublicGateway} onChange={(event) => settings.update({ showPublicGateway: event.target.checked })} /></label>
            <label><span><Eye /><span><strong>Privátní gateway IPv4</strong><small>Zobrazit IPv4 adresu gateway položky interního rozhraní.</small></span></span><input type="checkbox" checked={settings.showPrivateGateway} onChange={(event) => settings.update({ showPrivateGateway: event.target.checked })} /></label>
            <label><span><Network /><span><strong>Veřejná gateway IPv6</strong><small>Zobrazit IPv6 adresu gateway položky veřejného rozhraní.</small></span></span><input type="checkbox" checked={settings.showGatewayIpv6} onChange={(event) => settings.update({ showGatewayIpv6: event.target.checked })} /></label>
            <label><span><MonitorUp /><span><strong>VNC adresa</strong><small>Zobrazit adresu aktivní vzdálené konzole VNC.</small></span></span><input type="checkbox" checked={settings.showVncAddress} onChange={(event) => settings.update({ showVncAddress: event.target.checked })} /></label>
          </div>
        </section>

        <section className="detail-card auto-logout-card">
          <header className="detail-card__header">
            <div><span className="detail-card__icon"><Clock3 /></span><span><h2>Automatické odhlášení</h2><p>Ochrana účtu při nečinnosti</p></span></div>
          </header>
          <div className="auto-logout-setting">
            <label className="field" htmlFor="auto-logout-interval">
              <span>Odhlásit při nečinnosti</span>
              <select
                id="auto-logout-interval"
                value={settings.autoLogoutMinutes ?? "off"}
                onChange={(event) => settings.update({
                  autoLogoutMinutes: event.target.value === "off" ? null : normalizeAutoLogoutMinutes(event.target.value),
                })}
              >
                {AUTO_LOGOUT_OPTIONS.map((minutes) => <option key={minutes} value={minutes}>{minutes} minut</option>)}
                <option value="off">Neodhlašovat automaticky</option>
              </select>
            </label>
            <p>
              {settings.autoLogoutMinutes === null
                ? "Automatické odhlášení je vypnuté a odpočet se v horní liště nezobrazuje."
                : "Každý klik v aplikaci vrátí odpočet na začátek zvoleného intervalu."}
            </p>
          </div>
        </section>
      </div>

      <section className="detail-card session-card">
        <div><span><LogOut /></span><span><h2>Aktivní přihlášení</h2><p>Odhlášením odstraníte přístupové údaje z tohoto prohlížeče.</p></span></div>
        <Button variant="danger" onClick={() => { logout(); navigate("/"); }}>Odhlásit se</Button>
      </section>
    </div>
  );
}
